/**
 * Sertarul "Action" din celulele listelor, fara jQuery si fara bootstrap.js.
 *
 * Serverul trimite celula gata facuta, cu markupul de Bootstrap 3 al
 * portalului (`OverviewActions`, `DiscoveryActions`; la tichete il scrie
 * `easeedesk/columns.ts`): un `button.dropdown-toggle[data-toggle=dropdown]`
 * si un `ul.dropdown-menu` langa el, intr-un `div.btn-group`. `DataTable` il
 * pune in `td` cu `dangerouslySetInnerHTML`. In portal, `bootstrap.js` punea
 * clasa `open` pe parinte la clic, si de ea atarna `.open > .dropdown-menu {
 * display: block }` din `public/css/app.css`. In v4 nu ruleaza nici jQuery,
 * nici bootstrap.js, deci nimeni nu mai punea clasa: butonul se apasa si nu
 * se intampla nimic.
 *
 * Aici o pune un singur ascultator pe document, ca la baloane
 * (`tooltips.ts`): clic pe buton deschide sau inchide, clic in alta parte sau
 * Escape inchide, sagetile plimba prin intrari. Focusul e al butonului si al
 * legaturilor din meniu, deci se ajunge la ele si de la tastatura.
 *
 * Se uita NUMAI la sertarele dintr-o celula de rand (`td`, si nu
 * `td.rowDetails`), fiindca alea sunt HTML-ul venit de la server. Cele scrise
 * in React - `SelectPicker`, meniurile din `Shell`, sortarea din capul
 * tabelului - isi tin singure clasa `open` din stare si nu se ating: se
 * inchide doar sertarul pe care l-am deschis chiar noi.
 */

const TOGGLE = '[data-toggle="dropdown"]'
const HOLDER = '.btn-group, .dropdown, .dropup'
const ITEMS = '.dropdown-menu > li:not(.divider) > a'

export interface CellDropdown {
  holder: Element
  toggle: Element
}

let opened: CellDropdown | null = null

/** Sertarul celulei de sub `target`, sau null pentru orice altceva. */
export function cellDropdownAt(target: EventTarget | null): CellDropdown | null {
  if (!(target instanceof Element)) return null
  const toggle = target.closest(TOGGLE)
  if (!toggle) return null
  const cell = toggle.closest('td')
  if (!cell || cell.classList.contains('rowDetails')) return null
  const holder = toggle.closest(HOLDER)
  return holder ? { holder, toggle } : null
}

function itemsOf(holder: Element): HTMLElement[] {
  return Array.from(holder.querySelectorAll<HTMLElement>(ITEMS))
}

export function closeCellDropdown(): void {
  if (!opened) return
  opened.holder.classList.remove('open')
  opened.toggle.setAttribute('aria-expanded', 'false')
  opened = null
}

function openCellDropdown(found: CellDropdown): void {
  closeCellDropdown()
  found.holder.classList.add('open')
  found.toggle.setAttribute('aria-expanded', 'true')
  opened = found
}

function onClick(event: MouseEvent): void {
  const found = cellDropdownAt(event.target)
  if (found) {
    /* Butonul e `type=button`, dar toggle-ul poate fi si un `a href="#"`. */
    event.preventDefault()
    if (opened && opened.holder === found.holder) closeCellDropdown()
    else openCellDropdown(found)
    return
  }

  /* Clic pe o intrare, sau oriunde in afara: fapta e a intrarii, sertarul se
     inchide dupa - ca `clearMenus` din bootstrap.js. Legaturile moarte
     (`href="#"`) nu mai sar pagina in sus si nu mai lasa un `#` in adresa. */
  if (event.target instanceof Element && !event.target.closest('input, select, textarea, label')) {
    const dead = event.target.closest('.dropdown-menu a[href="#"]')
    if (dead && opened && opened.holder.contains(dead)) event.preventDefault()
  }
  closeCellDropdown()
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    if (!opened) return
    const toggle = opened.toggle
    closeCellDropdown()
    if (toggle instanceof HTMLElement) toggle.focus()
    event.preventDefault()
    return
  }

  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

  const inOpen = opened !== null && event.target instanceof Node && opened.holder.contains(event.target)
  if (!inOpen) {
    /* Sageata pe un buton inchis il deschide si intra pe prima intrare. */
    const found = cellDropdownAt(event.target)
    if (!found) return
    event.preventDefault()
    openCellDropdown(found)
    itemsOf(found.holder)[0]?.focus()
    return
  }

  const list = itemsOf(opened!.holder)
  if (list.length === 0) return
  event.preventDefault()
  const at = list.indexOf(document.activeElement as HTMLElement)
  const next = event.key === 'ArrowDown' ? (at + 1) % list.length : at <= 0 ? list.length - 1 : at - 1
  list[next]?.focus()
}

let installed = false

/** O singura data pe pagina; se poate chema de oriunde. */
export function installCellDropdowns(): () => void {
  if (installed) return () => {}
  installed = true
  document.addEventListener('click', onClick)
  document.addEventListener('keydown', onKeyDown)
  return () => {
    installed = false
    closeCellDropdown()
    document.removeEventListener('click', onClick)
    document.removeEventListener('keydown', onKeyDown)
  }
}
